/**
 * Device Performance Detection Utility
 * Checks if device has sufficient resources to handle animations
 */

export const DevicePerformance = {
  // Cache the performance check result
  _cachedResult: null,

  /**
   * Get device memory in GB (if available)
   */
  getDeviceMemory() {
    return navigator.deviceMemory || null;
  },

  /**
   * Get CPU core count
   */
  getCPUCores() {
    return navigator.hardwareConcurrency || 2;
  },

  /**
   * Check if connection is slow (cellular/2g/3g)
   */
  isSlowConnection() {
    if (!navigator.connection && !navigator.mozConnection && !navigator.webkitConnection) {
      return false; // Unknown, assume good
    }

    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const slowTypes = ['slow-2g', '2g', '3g', 'cellular'];
    
    return slowTypes.includes(connection.effectiveType) || 
           connection.saveData === true;
  },

  /**
   * Detect if device is mobile
   */
  isMobileDevice() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (window.innerWidth <= 768);
  },

  /**
   * Check GPU capabilities (basic check)
   */
  hasGoodGPU() {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    
    if (!gl) return false;

    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    if (debugInfo) {
      const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
      // Check for known low-end GPUs
      const lowEndGPUs = ['Mali-400', 'Mali-450', 'Adreno 304', 'Adreno 305', 'Adreno 306'];
      return !lowEndGPUs.some(gpu => renderer.includes(gpu));
    }

    return true; // Unknown, assume good
  },

  /**
   * Calculate performance score (0-100)
   */
  calculatePerformanceScore() {
    let score = 50; // Base score

    // Memory check (0-20 points)
    const memory = this.getDeviceMemory();
    if (memory !== null) {
      if (memory >= 8) score += 20;
      else if (memory >= 4) score += 15;
      else if (memory >= 2) score += 10;
      else score += 5;
    } else {
      score += 10; // Unknown, assume moderate
    }

    // CPU cores check (0-20 points)
    const cores = this.getCPUCores();
    if (cores >= 8) score += 20;
    else if (cores >= 4) score += 15;
    else if (cores >= 2) score += 10;
    else score += 5;

    // Connection check (-10 points if slow)
    if (this.isSlowConnection()) {
      score -= 10;
    }

    // Mobile penalty (-10 points)
    if (this.isMobileDevice()) {
      score -= 10;
    }

    // GPU check (0-10 points)
    if (this.hasGoodGPU()) {
      score += 10;
    }

    return Math.max(0, Math.min(100, score));
  },

  /**
   * Main function: Can device handle heavy animations?
   * @param {number} threshold - Minimum score required (default: 60)
   * @returns {boolean}
   */
  canHandleAnimation(threshold = 60) {
    // Return cached result if available
    if (this._cachedResult !== null) {
      return this._cachedResult;
    }

    const score = this.calculatePerformanceScore();
    console.log('📊 Device Performance Score:', score);
    console.log('📱 Device Info:', {
      memory: this.getDeviceMemory() + 'GB' || 'Unknown',
      cores: this.getCPUCores(),
      mobile: this.isMobileDevice(),
      slowConnection: this.isSlowConnection(),
      goodGPU: this.hasGoodGPU()
    });

    this._cachedResult = score >= threshold;
    return this._cachedResult;
  },

  /**
   * Get recommended animation settings based on performance
   */
  getRecommendedSettings() {
    const score = this.calculatePerformanceScore();

    if (score >= 80) {
      return {
        enabled: true,
        quality: 'high',
        particleCount: 600,
        fps: 60
      };
    } else if (score >= 60) {
      return {
        enabled: true,
        quality: 'medium',
        particleCount: 400,
        fps: 30
      };
    } else if (score >= 40) {
      return {
        enabled: true,
        quality: 'low',
        particleCount: 200,
        fps: 30
      };
    } else {
      return {
        enabled: false,
        quality: 'none',
        particleCount: 0,
        fps: 0
      };
    }
  },

  /**
   * Reset cached result (useful for testing)
   */
  reset() {
    this._cachedResult = null;
  }
};

export default DevicePerformance;
